import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import CopyIcon from './copy.svg?react';
import CheckIcon from './check.svg?react';
import useStyles from './copy-button.styles';

// Long enough to register at a glance, short enough to be gone before the next copy.
export const COPIED_DURATION_MS = 1200;

/**
 * The one copy-to-clipboard control in staff-fe. Confirms a successful copy by morphing its
 * icon into a checkmark and announcing "Copied" instead of showing a success toast.
 *
 * `onCopy` does the copying (and any prep it needs first, such as validation or building a link)
 * and returns, or resolves to, true only when the text actually reached the clipboard. Failure
 * feedback stays with the caller, so each surface keeps its existing error message.
 */
const CopyButton = forwardRef(function CopyButton(
  { onCopy, children, iconPosition, className, iconClassName, ...buttonProps },
  ref
) {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimeoutRef.current), []);

  const handleClick = async () => {
    if (!(await onCopy())) {
      return;
    }
    clearTimeout(resetTimeoutRef.current);
    setCopied(true);
    resetTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_DURATION_MS);
  };

  const icons = (
    <span className={classNames(classes.icons, iconClassName)} aria-hidden="true">
      <CopyIcon className={classNames(classes.icon, classes.copyIcon)} />
      <CheckIcon className={classNames(classes.icon, classes.checkIcon)} />
    </span>
  );

  return (
    <>
      <button
        type="button"
        ref={ref}
        {...buttonProps}
        className={classNames(classes.button, className)}
        data-state={copied ? 'copied' : 'idle'}
        onClick={handleClick}
      >
        {iconPosition === 'start' && icons}
        {children}
        {iconPosition === 'end' && icons}
      </button>
      <span role="status" className={classes.status}>
        {copied ? 'Copied' : ''}
      </span>
    </>
  );
});

CopyButton.propTypes = {
  onCopy: PropTypes.func.isRequired,
  children: PropTypes.node,
  iconPosition: PropTypes.oneOf(['start', 'end']),
  className: PropTypes.string,
  iconClassName: PropTypes.string
};

CopyButton.defaultProps = {
  children: null,
  iconPosition: 'end',
  className: undefined,
  iconClassName: undefined
};

export default CopyButton;
